import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  FiZap,
  FiPlus,
  FiSearch,
  FiX,
  FiTrash2,
  FiEdit2,
  FiSend,
  FiEye,
  FiToggleLeft,
  FiToggleRight,
  FiClock,
  FiGift,
  FiCalendar,
  FiMail,
  FiUsers,
  FiRefreshCw,
  FiChevronDown,
} from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CardSkeleton } from "@/components/common";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BlockEditorPanel, EMAIL_BRAND, blocksToMarkdown, markdownToBlocks, contentToHtml, interpolatePreview } from "@/components/email";
import type { ContentBlock } from "@/components/email";
import { getCronJob, nextRunMs, formatCountdown } from "./cron-schedule";

type Automation = {
  id: string;
  name: string;
  description: string | null;
  triggerType: string;
  triggerOffsetDays: number;
  triggerYears: number | null;
  subject: string;
  previewText: string | null;
  content: string;
  targetCustomerTypes: string | null;
  targetAccountStatuses: string | null;
  excludedCustomerIds: string | null;
  isEnabled: boolean;
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  createdAt: string;
};

type CustomerSummary = {
  id: string;
  email: string;
  name: string | null;
  accountStatus: string | null;
};

type AutomationTemplate = {
  name: string;
  description: string;
  triggerType: string;
  triggerYears?: number;
  subject: string;
  previewText: string;
  content: string;
};

const triggerTypeConfig: Record<string, { label: string; icon: React.ComponentType<{ className?: string }>; color: string }> = {
  birthday: { label: "Birthday", icon: FiGift, color: "bg-pink-100 text-pink-700" },
  settlement_anniversary: { label: "Anniversary", icon: FiCalendar, color: "bg-purple-100 text-purple-700" },
  welcome: { label: "Welcome", icon: FiUsers, color: "bg-green-100 text-green-700" },
  settlement_thank_you: { label: "Settlement", icon: FiMail, color: "bg-blue-100 text-blue-700" },
  christmas: { label: "Christmas", icon: FiGift, color: "bg-red-100 text-red-700" },
  rba_rate_change: { label: "Rate Change", icon: FiZap, color: "bg-amber-100 text-amber-700" },
  monthly_rba_update: { label: "RBA Update", icon: FiClock, color: "bg-cyan-100 text-cyan-700" },
};

export function AutomationsPage() {
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [templates, setTemplates] = useState<AutomationTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState<Automation | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingId, setIsTogglingId] = useState<string | null>(null);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [allCustomers, setAllCustomers] = useState<CustomerSummary[]>([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    triggerType: "birthday",
    triggerOffsetDays: 0,
    triggerYears: 3,
    subject: "",
    previewText: "",
    content: "",
    targetCustomerTypes: ["all"],
    targetAccountStatuses: ["all"],
    excludedCustomerIds: [] as string[],
    isEnabled: true,
  });

  const markdownContent = useMemo(() => blocksToMarkdown(blocks), [blocks]);

  // Generate live preview HTML matching the actual email rendering pipeline
  const previewContent = useMemo(() => {
    const subject = interpolatePreview(formData.subject);
    const content = interpolatePreview(markdownContent);
    const htmlContent = contentToHtml(content);
    return {
      subject,
      previewText: formData.previewText,
      html: htmlContent,
    };
  }, [formData.subject, markdownContent, formData.previewText]);

  const fetchAutomations = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/automations", {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json() as Automation[];
        setAutomations(data);
      }
    } catch (error) {
      console.error("Failed to fetch automations:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/automations/templates", {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json() as AutomationTemplate[];
        setTemplates(data);
      }
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/customers", { credentials: "include" });
      if (response.ok) {
        const data = (await response.json()) as CustomerSummary[];
        setAllCustomers(data);
      }
    } catch {
      // silently fail - customers list is supplementary
    }
  }, []);

  useEffect(() => {
    fetchAutomations();
    fetchTemplates();
    fetchCustomers();
  }, [fetchAutomations, fetchTemplates, fetchCustomers]);

  const filteredAutomations = automations.filter((automation) =>
    automation.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    automation.subject.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggle = async (automation: Automation) => {
    setIsTogglingId(automation.id);
    try {
      const response = await fetch(`/api/admin/automations/${automation.id}/toggle`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json() as { data: Automation };
        toast.success(`Automation ${data.data.isEnabled ? "enabled" : "disabled"}`);
        fetchAutomations();
      } else {
        toast.error("Failed to toggle automation");
      }
    } catch (error) {
      toast.error("Failed to toggle automation");
    } finally {
      setIsTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedAutomation) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`/api/admin/automations/${selectedAutomation.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Automation deleted");
        fetchAutomations();
        setDeleteModalOpen(false);
      } else {
        toast.error("Failed to delete automation");
      }
    } catch (error) {
      toast.error("Failed to delete automation");
    } finally {
      setIsDeleting(false);
      setSelectedAutomation(null);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject || !markdownContent) {
      toast.error("Name, subject, and content are required");
      return;
    }

    setIsSaving(true);
    try {
      const method = selectedAutomation ? "PUT" : "POST";
      const url = selectedAutomation
        ? `/api/admin/automations/${selectedAutomation.id}`
        : "/api/admin/automations";

      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, content: markdownContent }),
      });

      if (response.ok) {
        toast.success(selectedAutomation ? "Automation updated" : "Automation created");
        fetchAutomations();
        setEditorModalOpen(false);
        resetForm();
      } else {
        const data = await response.json() as { error?: string };
        toast.error(data.error || "Failed to save automation");
      }
    } catch (error) {
      toast.error("Failed to save automation");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!selectedAutomation) return;

    setIsSendingTest(true);
    try {
      const response = await fetch(`/api/admin/automations/${selectedAutomation.id}/test`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: testEmail || undefined }),
      });

      const data = await response.json() as { message?: string; error?: string };

      if (response.ok) {
        toast.success(data.message || "Test email sent");
        setTestModalOpen(false);
        setTestEmail("");
      } else {
        toast.error(data.error || "Failed to send test email");
      }
    } catch (error) {
      toast.error("Failed to send test email");
    } finally {
      setIsSendingTest(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      triggerType: "birthday",
      triggerOffsetDays: 0,
      triggerYears: 3,
      subject: "",
      previewText: "",
      content: "",
      targetCustomerTypes: ["all"],
      targetAccountStatuses: ["all"],
      excludedCustomerIds: [],
      isEnabled: true,
    });
    setBlocks([]);
    setSelectedAutomation(null);
    setCustomerSearch("");
    setShowCustomerList(false);
  };

  const openEditor = (automation?: Automation) => {
    if (automation) {
      setSelectedAutomation(automation);
      // Fix singular/plural on load for year=1
      const fixPlural = (text: string, y: number) => {
        if (y === 1) return text.replace(/\b1[- ]Years\b/g, "1-Year").replace(/\b1 years\b/gi, "1 year");
        return text;
      };
      const y = automation.triggerYears || 3;
      setFormData({
        name: fixPlural(automation.name, y),
        description: fixPlural(automation.description || "", y),
        triggerType: automation.triggerType,
        triggerOffsetDays: automation.triggerOffsetDays || 0,
        triggerYears: y,
        subject: fixPlural(automation.subject, y),
        previewText: fixPlural(automation.previewText || "", y),
        content: fixPlural(automation.content, y),
        targetCustomerTypes: automation.targetCustomerTypes
          ? JSON.parse(automation.targetCustomerTypes)
          : ["all"],
        targetAccountStatuses: automation.targetAccountStatuses
          ? JSON.parse(automation.targetAccountStatuses)
          : ["all"],
        excludedCustomerIds: automation.excludedCustomerIds
          ? JSON.parse(automation.excludedCustomerIds)
          : [],
        isEnabled: automation.isEnabled,
      });
      setBlocks(markdownToBlocks(fixPlural(automation.content, y)));
    } else {
      resetForm();
    }
    setShowPreview(true);
    setEditorModalOpen(true);
  };

  const applyTemplate = (template: AutomationTemplate) => {
    setFormData({
      ...formData,
      name: template.name,
      description: template.description,
      triggerType: template.triggerType,
      triggerYears: template.triggerYears || 3,
      subject: template.subject,
      previewText: template.previewText,
      content: template.content,
    });
    setBlocks(markdownToBlocks(template.content));
    toast.success("Template applied");
  };

  const handleScanNow = async () => {
    setIsScanning(true);
    try {
      const response = await fetch("/api/admin/automations/scan", {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json() as {
        message?: string;
        error?: string;
        queued?: number;
        details?: string[];
        data?: {
          queued?: number;
          details?: string[];
          debug?: {
            serverTime?: string;
            serverDateLocal?: string;
            lookaheadDates?: string[];
            enabledAutomations?: { name: string; type: string; enabled?: boolean }[];
            skipped?: string[];
          };
        };
      };

      if (response.ok) {
        const queued = data.data?.queued ?? data.queued ?? 0;
        const debug = data.data?.debug;

        if (queued > 0) {
          toast.success(`Scan complete: ${queued} emails queued`);
        } else {
          // Show debug info when nothing queued
          const skippedReason = debug?.skipped?.[0] || "";
          toast.info(`No emails to queue. ${skippedReason}`);
        }
        // Always log debug info to console
        console.log("Scan debug info:", debug);
      } else {
        toast.error(data.error || "Failed to run scan");
      }
    } catch (error) {
      toast.error("Failed to run scan");
    } finally {
      setIsScanning(false);
    }
  };

  const stats = {
    total: automations.length,
    enabled: automations.filter((a) => a.isEnabled).length,
    totalSent: automations.reduce((sum, a) => sum + (a.totalSent || 0), 0),
  };

  // Cron countdowns — schedule + cadence labels come from the shared
  // cron-schedule module so this page never drifts from the deployed
  // wrangler.toml triggers.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const processAutomationsJob = useMemo(() => getCronJob("process-automations"), []);
  const sendEmailsJob = useMemo(() => getCronJob("send-scheduled-emails"), []);
  const nextProcessAutomations = useMemo(
    () => nextRunMs(processAutomationsJob.cadence, now),
    [processAutomationsJob, now],
  );
  const nextSendEmails = useMemo(
    () => nextRunMs(sendEmailsJob.cadence, now),
    [sendEmailsJob, now],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-medium text-slate-900">
            Email Automations
          </h1>
          <p className="text-muted-foreground mt-1">
            Automated emails for birthdays, anniversaries, and more
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleScanNow} disabled={isScanning}>
            <FiRefreshCw className={`w-4 h-4 mr-2 ${isScanning ? "animate-spin" : ""}`} />
            {isScanning ? "Scanning..." : "Scan Now"}
          </Button>
          <Button onClick={() => openEditor()}>
            <FiPlus className="w-4 h-4 mr-2" />
            New Automation
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FiZap className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiToggleRight className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.enabled}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiSend className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalSent}</p>
                <p className="text-xs text-muted-foreground">Emails Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cron Job Countdowns */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FiRefreshCw className="w-4 h-4" />
            Automated Background Jobs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                <FiZap className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{processAutomationsJob.label}</p>
                <p className="text-xs text-muted-foreground">{processAutomationsJob.description}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold tabular-nums text-blue-700">{formatCountdown(nextProcessAutomations, now)}</p>
                <p className="text-[10px] text-muted-foreground">{processAutomationsJob.cadenceLabel}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <div className="p-2 bg-green-100 rounded-lg shrink-0">
                <FiSend className="w-4 h-4 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{sendEmailsJob.label}</p>
                <p className="text-xs text-muted-foreground">{sendEmailsJob.description}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold tabular-nums text-green-700">{formatCountdown(nextSendEmails, now)}</p>
                <p className="text-[10px] text-muted-foreground">{sendEmailsJob.cadenceLabel}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Search automations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <FiX className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Automations List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FiZap className="w-5 h-5" />
            All Automations ({filteredAutomations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : filteredAutomations.length === 0 ? (
            <div className="text-center py-12">
              <FiZap className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground mb-4">
                {searchQuery ? "No automations found" : "No automations yet"}
              </p>
              {!searchQuery && (
                <Button onClick={() => openEditor()}>
                  <FiPlus className="w-4 h-4 mr-2" />
                  Create Your First Automation
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAutomations.map((automation) => {
                const config = triggerTypeConfig[automation.triggerType] || {
                  label: automation.triggerType,
                  icon: FiMail,
                  color: "bg-gray-100 text-gray-700",
                };
                const TriggerIcon = config.icon;

                return (
                  <div
                    key={automation.id}
                    className={`flex flex-col lg:flex-row lg:items-center gap-4 p-4 rounded-lg transition-colors ${
                      automation.isEnabled
                        ? "bg-muted/50 hover:bg-muted/70"
                        : "bg-gray-100 opacity-60"
                    }`}
                  >
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg shrink-0 ${config.color}`}>
                          <TriggerIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium truncate">{automation.name}</h3>
                            <Badge variant="secondary" className={config.color}>
                              {config.label}
                            </Badge>
                            {!automation.isEnabled && (
                              <Badge variant="secondary" className="bg-gray-200 text-gray-600">
                                Disabled
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate mt-0.5">
                            {automation.subject}
                          </p>
                          {automation.description && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {automation.description}
                            </p>
                          )}
                          {automation.targetAccountStatuses && automation.targetAccountStatuses.startsWith("[") && (() => {
                            const statuses = JSON.parse(automation.targetAccountStatuses) as string[];
                            if (statuses.includes("all")) return null;
                            const statusLabels: Record<string, string> = { existing: "Existing", ineligible: "Ineligible", discharged: "Discharged" };
                            const statusColors: Record<string, string> = { existing: "bg-emerald-100 text-emerald-700", ineligible: "bg-amber-100 text-amber-700", discharged: "bg-slate-100 text-slate-600" };
                            return (
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-xs text-muted-foreground">Targets:</span>
                                {statuses.map((s) => (
                                  <span key={s} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColors[s] || ""}`}>
                                    {statusLabels[s] || s}
                                  </span>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    {automation.totalSent > 0 && (
                      <div className="flex items-center gap-6 px-4 py-2 bg-background rounded-lg border">
                        <div className="text-center">
                          <p className="text-lg font-bold">{automation.totalSent}</p>
                          <p className="text-xs text-muted-foreground">Sent</p>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggle(automation)}
                        disabled={isTogglingId === automation.id}
                        title={automation.isEnabled ? "Disable" : "Enable"}
                      >
                        {automation.isEnabled ? (
                          <FiToggleRight className="w-5 h-5 text-green-600" />
                        ) : (
                          <FiToggleLeft className="w-5 h-5 text-gray-400" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedAutomation(automation);
                          setTestModalOpen(true);
                        }}
                        title="Send Test"
                      >
                        <FiSend className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditor(automation)}
                        title="Edit"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedAutomation(automation);
                          setDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editor Modal - Full Screen with Preview */}
      <Dialog open={editorModalOpen} onOpenChange={setEditorModalOpen}>
        <DialogContent
          className="!w-[95vw] !max-w-[95vw] !h-[90vh] !max-h-[90vh] !p-0 !gap-0 flex flex-col"
          showCloseButton={false}
        >
          {/* Modal Header */}
          <div className="shrink-0 flex items-center justify-between px-6 py-3 border-b border-border bg-white rounded-t-2xl">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-muted-foreground hover:text-foreground rounded-lg"
                onClick={() => setEditorModalOpen(false)}
              >
                <FiX className="w-4 h-4" />
                Close
              </Button>
              <DialogTitle className="font-serif text-xl font-medium text-foreground">
                {selectedAutomation ? "Edit Automation" : "New Automation"}
              </DialogTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={showPreview ? "default" : "outline"}
                size="sm"
                onClick={() => setShowPreview(!showPreview)}
                className="hidden lg:flex gap-1.5"
              >
                <FiEye className="w-3.5 h-3.5" />
                Preview
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
                className="gap-1.5"
              >
                {isSaving ? "Saving..." : selectedAutomation ? "Update" : "Create"}
              </Button>
            </div>
          </div>

          {/* Content Area - Both panels independently scrollable */}
          <div className={`flex-1 min-h-0 overflow-hidden grid ${showPreview ? "lg:grid-cols-2 grid-cols-1" : "grid-cols-1"}`}>
            {/* Editor Panel */}
            <div className="h-full overflow-y-auto bg-muted/30">
              <div className="max-w-2xl mx-auto p-6 md:p-8 space-y-6">

                {/* ── Step 1: Setup ── */}
                <section className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
                  <div className="flex items-center gap-3 px-6 py-4 bg-secondary/50 border-b border-border">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-[11px] font-bold">1</div>
                    <h2 className="font-serif text-lg font-medium text-foreground">Setup</h2>
                  </div>

                  <div className="p-6 space-y-5">
                    {/* Template Selection */}
                    {!selectedAutomation && templates.length > 0 && (
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5 block">Start from a template</label>
                        <Select onValueChange={(value) => {
                          const template = templates.find(t => t.name === value);
                          if (template) applyTemplate(template);
                        }}>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a template..." />
                          </SelectTrigger>
                          <SelectContent>
                            {templates.map((template) => (
                              <SelectItem key={template.name} value={template.name}>
                                {template.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-foreground/70 mb-1.5 block">Name *</label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Birthday Greetings"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground/70 mb-1.5 block">Trigger Type</label>
                        <Select
                          value={formData.triggerType}
                          onValueChange={(value) => setFormData({ ...formData, triggerType: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="birthday">Birthday</SelectItem>
                            <SelectItem value="settlement_anniversary">Settlement Anniversary</SelectItem>
                            <SelectItem value="welcome">Welcome (New Customer)</SelectItem>
                            <SelectItem value="settlement_thank_you">Settlement Thank You</SelectItem>
                            <SelectItem value="christmas">Christmas</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {formData.triggerType === "settlement_anniversary" && (
                      <div>
                        <label className="text-sm font-medium text-foreground/70 mb-1.5 block">Anniversary Year</label>
                        <Input
                          type="number"
                          min={1}
                          max={30}
                          value={formData.triggerYears}
                          onChange={(e) => {
                            const newYears = parseInt(e.target.value) || 3;
                            const oldYears = formData.triggerYears;
                            if (newYears === oldYears) return;
                            const updates: Partial<typeof formData> = { triggerYears: newYears };
                            // Replace all references to the old year number with correct singular/plural
                            const yearWord = newYears === 1 ? "Year" : "Years";
                            const yearWordLower = newYears === 1 ? "year" : "years";
                            const replaceYears = (text: string) =>
                              text
                                .replace(new RegExp(`\\b${oldYears}[- ]?[Yy]ears?`, "g"), `${newYears}-${yearWord}`)
                                .replace(new RegExp(`\\b${oldYears} years?\\b`, "gi"), `${newYears} ${yearWordLower}`)
                                .replace(new RegExp(`\\b${oldYears}(\\s+)years?\\b`, "gi"), `${newYears}$1${yearWordLower}`);
                            updates.name = replaceYears(formData.name);
                            updates.description = replaceYears(formData.description);
                            updates.subject = replaceYears(formData.subject);
                            updates.previewText = replaceYears(formData.previewText);
                            // Update block content too
                            setBlocks(prev => prev.map(block => ({
                              ...block,
                              content: replaceYears(block.content),
                              ...(block.content2 ? { content2: replaceYears(block.content2) } : {}),
                            })));
                            setFormData({ ...formData, ...updates });
                          }}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Send after X years since settlement
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="text-sm font-medium text-foreground/70 mb-1.5 block">Description</label>
                      <Input
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Short description of this automation"
                      />
                    </div>

                    {/* Account Status Targeting */}
                    <div>
                      <label className="text-sm font-medium text-foreground/70 mb-1.5 block">Target Customer Groups</label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Choose which groups receive this automation.
                      </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: "all", label: "All Customers", color: "bg-blue-100 text-blue-700 border-blue-200" },
                      { value: "existing", label: "Existing (Paying)", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
                      { value: "ineligible", label: "Ineligible", color: "bg-amber-100 text-amber-700 border-amber-200" },
                      { value: "discharged", label: "Discharged", color: "bg-slate-100 text-slate-600 border-slate-300" },
                    ].map(({ value, label, color }) => {
                      const isAll = value === "all";
                      const allSelected = formData.targetAccountStatuses.includes("all");
                      const isSelected = isAll ? allSelected : formData.targetAccountStatuses.includes(value);

                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            if (isAll) {
                              setFormData({ ...formData, targetAccountStatuses: ["all"] });
                            } else {
                              let next = allSelected
                                ? [value]
                                : isSelected
                                  ? formData.targetAccountStatuses.filter((s) => s !== value)
                                  : [...formData.targetAccountStatuses.filter((s) => s !== "all"), value];
                              if (next.length === 0) next = ["all"];
                              if (next.length === 3 && !next.includes("all")) next = ["all"];
                              setFormData({ ...formData, targetAccountStatuses: next });
                            }
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                            isSelected || (isAll && allSelected)
                              ? color
                              : "bg-white text-gray-400 border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-3" />

                  {/* Individual customer control */}
                  {(() => {
                    const activeStatuses = formData.targetAccountStatuses.includes("all")
                      ? ["existing", "ineligible", "discharged"]
                      : formData.targetAccountStatuses;
                    const matchingCustomers = allCustomers.filter((c) =>
                      activeStatuses.includes(c.accountStatus || "")
                    );
                    const filtered = customerSearch
                      ? matchingCustomers.filter((c) =>
                          c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
                          c.email.toLowerCase().includes(customerSearch.toLowerCase())
                        )
                      : matchingCustomers;
                    const activeCount = matchingCustomers.length - formData.excludedCustomerIds.length;

                    return (
                      <div className="rounded-xl border border-border bg-secondary/30 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setShowCustomerList(!showCustomerList)}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/60 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <FiUsers className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium text-foreground/80">Recipients</span>
                            <span className="text-xs text-muted-foreground">
                              {activeCount} of {matchingCustomers.length}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {formData.excludedCustomerIds.length > 0 && (
                              <span className="text-[11px] font-medium text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                                {formData.excludedCustomerIds.length} excluded
                              </span>
                            )}
                            <FiChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${showCustomerList ? "rotate-180" : ""}`} />
                          </div>
                        </button>

                        {showCustomerList && (
                          <>
                            <div className="px-3 pb-2 border-t border-border/50 pt-2">
                              <div className="relative">
                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-3.5 h-3.5" />
                                <Input
                                  placeholder="Search customers..."
                                  value={customerSearch}
                                  onChange={(e) => setCustomerSearch(e.target.value)}
                                  className="pl-9 h-9 text-sm bg-white"
                                />
                              </div>
                            </div>
                            <div className="max-h-52 overflow-y-auto">
                              {filtered.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-6">
                                  {customerSearch ? "No customers match search" : "No customers in selected groups"}
                                </p>
                              ) : (
                                filtered.map((customer) => {
                                  const isExcluded = formData.excludedCustomerIds.includes(customer.id);
                                  return (
                                    <label
                                      key={customer.id}
                                      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer border-t border-border/30 hover:bg-white/60 transition-colors ${isExcluded ? "opacity-40" : ""}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={!isExcluded}
                                        onChange={() => {
                                          setFormData({
                                            ...formData,
                                            excludedCustomerIds: isExcluded
                                              ? formData.excludedCustomerIds.filter((id) => id !== customer.id)
                                              : [...formData.excludedCustomerIds, customer.id],
                                          });
                                        }}
                                        className="h-4 w-4 rounded border-border accent-primary"
                                      />
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-foreground truncate">{customer.name || customer.email}</p>
                                        {customer.name && (
                                          <p className="text-xs text-muted-foreground truncate">{customer.email}</p>
                                        )}
                                      </div>
                                      {customer.accountStatus && (
                                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${
                                          customer.accountStatus === "existing" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                                          customer.accountStatus === "ineligible" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                                          "bg-slate-50 text-slate-600 border border-slate-200"
                                        }`}>
                                          {customer.accountStatus}
                                        </span>
                                      )}
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                    </div>
                  </div>
                </section>

                {/* ── Step 2: Email Content ── */}
                <section className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
                  <div className="flex items-center gap-3 px-6 py-4 bg-secondary/50 border-b border-border">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-[11px] font-bold">2</div>
                    <h2 className="font-serif text-lg font-medium text-foreground">Email Content</h2>
                  </div>

                  <div className="p-6 space-y-5">
                    <div>
                      <label className="text-sm font-medium text-foreground/70 mb-1.5 block">Email Subject *</label>
                      <Input
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="Happy Birthday, {{firstName}}!"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Variables: {"{{name}}"}, {"{{firstName}}"}, {"{{preferredName}}"}, {"{{email}}"}, {"{{loanType}}"}, {"{{years}}"}, {"{{allLoans}}"}, {"{{loanCount}}"}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-foreground/70 mb-1.5 block">
                        Preview Text
                        <span className="text-muted-foreground font-normal ml-1 text-xs">optional</span>
                      </label>
                      <Input
                        value={formData.previewText}
                        onChange={(e) => setFormData({ ...formData, previewText: e.target.value })}
                        placeholder="Shown in inbox after subject line"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-foreground/70 mb-1.5 block">Email Content *</label>
                      <BlockEditorPanel blocks={blocks} onBlocksChange={setBlocks} mediaFolder="general" />
                    </div>
                  </div>
                </section>

              </div>
            </div>

            {/* Preview Panel — mirrors wrapEmail() from layout.ts */}
            {showPreview && (
              <div className="hidden lg:flex flex-col h-full overflow-hidden border-l border-border bg-muted/40">
                <div className="flex-1 overflow-y-auto">
                  <div className="p-6 pb-12">
                  {/* Email Client Mockup */}
                  <div className="rounded-2xl shadow-lg overflow-hidden border border-border max-w-xl mx-auto bg-white">
                    {/* From / Subject bar */}
                    <div className="px-5 py-4 border-b bg-white">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0" style={{ backgroundColor: EMAIL_BRAND.primary }}>
                          PF
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground">{EMAIL_BRAND.companyName}</p>
                          <p className="text-xs text-muted-foreground">hello@pathfinancial.com.au</p>
                        </div>
                      </div>
                      <p className="font-semibold text-foreground">
                        {previewContent.subject || "Your subject line will appear here"}
                      </p>
                      {previewContent.previewText && (
                        <p className="text-sm text-muted-foreground mt-0.5 truncate">
                          {previewContent.previewText}
                        </p>
                      )}
                    </div>

                    {/* Actual email layout — matches wrapEmail() */}
                    <div className="flex justify-center" style={{ backgroundColor: EMAIL_BRAND.cream, padding: '40px 20px' }}>
                      <div style={{ maxWidth: 600, width: '100%', backgroundColor: EMAIL_BRAND.white, borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        {/* Logo header */}
                        <div style={{ textAlign: 'center', padding: '28px 40px 20px 40px', backgroundColor: EMAIL_BRAND.white }}>
                          <img src={EMAIL_BRAND.logoUrl} alt={EMAIL_BRAND.companyName} style={{ display: 'block', width: 220, maxWidth: '100%', height: 'auto', margin: '0 auto' }} />
                        </div>

                        {/* Accent line */}
                        <div style={{ padding: '0 40px' }}>
                          <div style={{ borderTop: `3px solid ${EMAIL_BRAND.primary}` }} />
                        </div>

                        {/* Content */}
                        <div style={{ padding: '32px 40px 40px 40px' }}>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: previewContent.html || `<p style="color: ${EMAIL_BRAND.textLight}; font-style: italic;">(Start typing to see preview)</p>`,
                            }}
                          />
                        </div>

                        {/* Divider */}
                        <div style={{ padding: '0 40px' }}>
                          <div style={{ borderTop: `1px solid ${EMAIL_BRAND.border}` }} />
                        </div>

                        {/* Footer */}
                        <div style={{ padding: '28px 40px', backgroundColor: EMAIL_BRAND.cream, textAlign: 'center', fontSize: 13, color: EMAIL_BRAND.textLight, lineHeight: 1.6 }}>
                          <p style={{ margin: '0 0 6px 0' }}>
                            <strong style={{ color: EMAIL_BRAND.primary }}>{EMAIL_BRAND.companyName}</strong>
                          </p>
                          <p style={{ margin: '0 0 6px 0' }}>
                            Australian Credit Licence {EMAIL_BRAND.acl} | ABN {EMAIL_BRAND.abn}
                          </p>
                          <p style={{ margin: '0 0 12px 0' }}>
                            {EMAIL_BRAND.address}
                          </p>
                          <p style={{ margin: 0 }}>
                            <a href="#" style={{ color: EMAIL_BRAND.accent, textDecoration: 'underline' }}>Manage Preferences</a>
                            &nbsp;&nbsp;|&nbsp;&nbsp;
                            <a href="#" style={{ color: EMAIL_BRAND.accent, textDecoration: 'underline' }}>Unsubscribe</a>
                          </p>
                        </div>

                        {/* Copyright */}
                        <div style={{ textAlign: 'center', padding: '12px 40px 20px 40px' }}>
                          <p style={{ margin: 0, fontSize: 11, color: EMAIL_BRAND.textLight }}>
                            &copy; {new Date().getFullYear()} {EMAIL_BRAND.companyName}. All rights reserved.
                          </p>
                          <p style={{ margin: '4px 0 0 0', fontSize: 11 }}>
                            <a href={EMAIL_BRAND.siteUrl} style={{ color: EMAIL_BRAND.textLight, textDecoration: 'underline' }}>pathfinancial.com.au</a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <p className="text-center text-[11px] text-muted-foreground mt-4">
                    Variables like {"{{firstName}}"} show as sample data in preview
                  </p>
                  </div>
                </div>
              </div>
            )}
            </div>
        </DialogContent>
      </Dialog>

      {/* Test Email Modal */}
      <Dialog open={testModalOpen} onOpenChange={setTestModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test email to verify the automation looks correct
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium">Email Address</label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Leave empty to send to your admin email"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendTest} disabled={isSendingTest}>
              <FiSend className="w-4 h-4 mr-2" />
              {isSendingTest ? "Sending..." : "Send Test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Automation</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this automation? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedAutomation && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="font-medium">{selectedAutomation.name}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedAutomation.totalSent} emails sent
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
